import React, { useContext } from "react";
import styled from "styled-components";
import { hexToRGB } from "@shared/utils/misc";
import { DiscountTag } from "features/settings/Subscription/styles";
import Flex from "common/Boxes/Flex";
import {
  EXTEND_SUBSCRIPTION_DURATIONS,
  LanguageNameSpaces,
  SubscriptionDuration,
} from "@shared/types";
import { DURATION_TO_SECONDARY_DISPLAY } from "@shared/features/settings/subscription/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context";

interface PeriodButtonsProps {
  promoCodeDiscount?: Record<SubscriptionDuration, number>;
}

const PeriodButtons = ({ promoCodeDiscount }: PeriodButtonsProps) => {
  const { duration, setDuration, promoCode } = useContext(
    BuySubscriptionContext
  )!;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const showAnnuallyDiscount =
    !!promoCode &&
    duration !== 360 &&
    !promoCodeDiscount?.[duration] &&
    !!promoCodeDiscount?.[360];

  const discountTagContent = !!promoCode ? (
    <>
      <PromoCode>{promoCode?.toUpperCase()}</PromoCode>
      {showAnnuallyDiscount
        ? t("period_buttons.annually_discount", {
            percent: promoCodeDiscount?.[360],
          })
        : t("period_buttons.discount_applied", {
            percent: promoCodeDiscount?.[duration] || 0,
          })}
    </>
  ) : (
    <>
      {t("period_buttons.annually_default")}{" "}
      <span style={{ fontWeight: 700 }}>{t("period_buttons.months_free")}</span>
    </>
  );

  return (
    <>
      <ButtonGroup>
        {EXTEND_SUBSCRIPTION_DURATIONS.map((parsedCurrentDuration) => {
          const displayName =
            DURATION_TO_SECONDARY_DISPLAY[parsedCurrentDuration];
          return (
            <StyledButton
              key={parsedCurrentDuration}
              $isSelected={parsedCurrentDuration === duration}
              onClick={() => setDuration(parsedCurrentDuration)}
            >
              {t(displayName)}
            </StyledButton>
          );
        })}
      </ButtonGroup>
      <Flex $justifyContent="center">
        <DiscountTag
          style={{ marginTop: 12, width: "fit-content", padding: "8px 16px" }}
          $isPromoCode={!!promoCode}
        >
          {discountTagContent}
        </DiscountTag>
      </Flex>
    </>
  );
};

export default PeriodButtons;

const ButtonGroup = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  border: ${(p) => p.theme.borders.light};
  padding: 4px;
  display: inline-flex;
  position: relative;
`;
const StyledButton = styled.div<{ $isSelected?: boolean }>`
  padding: 4px 12px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  cursor: pointer;
  transition: 0.35s;
  border-radius: 4px;
  color: ${(p) => p.theme.colors.primaryText};
  &:not(:last-of-type) {
    margin-right: 4px;
  }
  &:hover {
    background-color: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.1)};
  }
  ${(p) =>
    p.$isSelected &&
    `
    background-color: ${p.theme.colors.primaryBlue} !important;
    color: ${p.theme.colors.buttonText} !important;
  `}
`;
const PromoCode = styled.span`
  border: 1px dashed ${(p) => p.theme.colors.green};
  border-radius: 2px;
  padding: 2px 8px;
  margin-right: 8px;
`;
